// Správa v nastavení (jen správci): uživatelé a role, pravidla brány, přístupy všech, historie změn.
// Práva k datům ve zdrojích (Prdel) se spravují v Toolboxu, tady ne.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DotsThreeIcon, MagnifyingGlassIcon, PlusIcon, ProhibitIcon, ShieldCheckIcon, ShieldSlashIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { Badge, Button, Checkbox, DropdownMenu, EmptyState, Input, Pagination, Select, Spinner, useConfirm, useToast } from '@smworks-cz/ui-kit';
import { ROLE_LABEL, ROLES, toolLabel, type AdminEventDTO, type AdminUserDTO, type AuditEntryDTO, type MeDTO, type Role, type RuleDTO } from '@sam/shared';
import { api } from '../lib/api';
import { queryClient } from '../lib/queries';
import { fmtDateTime, fmtMs, fmtParams, reasonText } from '../lib/format';

const ROLE_OPTIONS = ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }));
const USERS_KEY = ['admin', 'users'];

const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

function useAdminUsers() {
  return useQuery({ queryKey: USERS_KEY, queryFn: async () => (await api<{ users: AdminUserDTO[] }>('GET', '/admin/users')).users, staleTime: 0 });
}

/* ---------- uživatelé ---------- */

export function AdminUsers({ me }: { me: MeDTO }) {
  const { data, isLoading, error } = useAdminUsers();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const confirm = useConfirm();
  const { toast } = useToast();

  const patch = async (u: AdminUserDTO, p: { role?: Role; isAdmin?: boolean; blocked?: boolean }) => {
    try {
      await api('PATCH', `/admin/users/${u.id}`, p);
      queryClient.setQueryData<AdminUserDTO[]>(USERS_KEY, (old) =>
        old?.map((x) => (x.id === u.id ? { ...x, ...(p.role ? { role: p.role } : {}), ...(p.isAdmin !== undefined ? { isAdmin: p.isAdmin } : {}), ...(p.blocked !== undefined ? { blocked: p.blocked } : {}) } : x)),
      );
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] });
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    }
  };

  const block = async (u: AdminUserDTO) => {
    const ok = await confirm({
      title: `Zablokovat ${u.name}?`,
      description: 'Okamžitě se odhlásí a do appky se nepřihlásí, dokud ho neodblokujete. Historie chatů zůstane.',
      confirmLabel: 'Zablokovat',
      cancelLabel: 'Zrušit',
      variant: 'danger',
    });
    if (ok) void patch(u, { blocked: true });
  };

  const shown = useMemo(() => {
    const nq = norm(q.trim());
    return (data ?? []).filter((u) => !nq || norm(`${u.name} ${u.email}`).includes(nq));
  }, [data, q]);

  return (
    <div className="set-list">
      <p className="muted">
        Role určuje, k jakým nástrojům a datům Sam za člověka sáhne (přehled v sekci Role a pravidla). Nový člověk z firmy se při prvním přihlášení
        přes Microsoft založí sám s rolí Realizační tým; předem přidaného spáruje podle e-mailu. Každá změna se zapíše do historie správy.
      </p>
      <div className="adm-bar">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat jméno nebo e-mail" icon={<MagnifyingGlassIcon size={14} />} clearable aria-label="Hledat uživatele" />
        <Button size="sm" icon={<PlusIcon size={15} />} onClick={() => setAdding((x) => !x)}>
          Přidat uživatele
        </Button>
      </div>
      {adding && <AddUser onDone={() => setAdding(false)} />}
      {isLoading && <Spinner />}
      {error && <p className="bad">{(error as Error).message}</p>}
      {data && !shown.length && <EmptyState size="sm" preset="no-results" title="Nikdo takový" />}
      <div className="adm-users">
        {shown.map((u) => {
          const self = u.id === me.user.id;
          return (
            <div key={u.id} className={`adm-user${u.blocked ? ' off' : ''}`}>
              <div className="adm-user-t">
                <b>
                  {u.name}
                  {self && <span className="muted"> (vy)</span>}
                </b>
                <small>
                  {u.email} · {u.lastLoginAt ? `naposledy ${fmtDateTime(u.lastLoginAt)}` : 'ještě se nepřihlásil'}
                </small>
              </div>
              <div className="adm-user-b">
                {u.isAdmin && <Badge label="správce" variant="brand" />}
                {u.blocked && <Badge label="zablokovaný" variant="danger" />}
              </div>
              <div className="adm-role">
                <Select options={ROLE_OPTIONS} value={u.role} onChange={(r) => r !== u.role && void patch(u, { role: r as Role })} disabled={u.blocked} aria-label={`Role ${u.name}`} />
              </div>
              <DropdownMenu
                position="bottom-right"
                trigger={
                  <button type="button" className="nav-more adm-more" aria-label={`Další akce pro ${u.name}`}>
                    <DotsThreeIcon size={18} weight="bold" />
                  </button>
                }
                items={[
                  u.isAdmin
                    ? { label: 'Odebrat správce', icon: <ShieldSlashIcon size={16} />, disabled: self, onClick: () => void patch(u, { isAdmin: false }) }
                    : { label: 'Nastavit jako správce', icon: <ShieldCheckIcon size={16} />, disabled: u.blocked, onClick: () => void patch(u, { isAdmin: true }) },
                  u.blocked
                    ? { label: 'Odblokovat', icon: <CheckCircleIcon size={16} />, onClick: () => void patch(u, { blocked: false }) }
                    : { label: 'Zablokovat přístup', icon: <ProhibitIcon size={16} />, danger: true, disabled: self, onClick: () => void block(u) },
                ]}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddUser({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('tym');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const save = async () => {
    setSaving(true);
    try {
      await api('POST', '/admin/users', { email, role, ...(name.trim() ? { name: name.trim() } : {}) });
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] });
      toast({ variant: 'success', title: `${email} přidán. Přihlásí se přes Microsoft.` });
      onDone();
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="adm-add"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <Input label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jmeno@smworks.cz" required />
      <Input label="Jméno (nepovinné)" value={name} onChange={(e) => setName(e.target.value)} placeholder="Doplní se z Microsoftu" />
      <div className="adm-add-role">
        <span className="adm-label">Role</span>
        <Select options={ROLE_OPTIONS} value={role} onChange={(r) => setRole(r as Role)} aria-label="Role nového uživatele" />
      </div>
      <div className="adm-add-actions">
        <Button variant="outline" size="sm" type="button" onClick={onDone}>
          Zrušit
        </Button>
        <Button size="sm" type="submit" loading={saving} disabled={!/^\S+@\S+\.\S+$/.test(email)}>
          Přidat
        </Button>
      </div>
    </form>
  );
}

/* ---------- role a pravidla ---------- */

export function AdminRules() {
  const q = useQuery({
    queryKey: ['admin', 'rules'],
    queryFn: () => api<{ rules: RuleDTO[]; tools: Record<string, { done: string; source: string }> }>('GET', '/admin/rules'),
  });
  return (
    <div className="set-list">
      <p className="muted">
        Co brána pustí jednotlivým rolím. Platí „zakázáno, dokud není povoleno“: nástroj bez pravidla nepustí nikomu. Pravidla jsou v kódu brány
        (`apps/brana/src/rules.ts`), aby každá změna prošla kontrolou a byla dohledatelná. K datům ve zdrojích má navíc vlastní práva Prdel (spravuje se v Toolboxu).
      </p>
      {q.isLoading && <Spinner />}
      {q.error && <p className="bad">{(q.error as Error).message}</p>}
      {q.data && (
        <div className="audit">
          <table>
            <thead>
              <tr>
                <th>Nástroj</th>
                {ROLES.map((r) => (
                  <th key={r} className="c">
                    {ROLE_LABEL[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.rules.map((r) => (
                <tr key={r.tool}>
                  <td>
                    {q.data.tools[r.tool]?.done ?? toolLabel(r.tool).done}
                    <div className="muted small">{r.why}</div>
                  </td>
                  {ROLES.map((role) => (
                    <td key={role} className="c">
                      {r.roles.includes(role) ? <span className="ok" aria-label="povoleno">✓</span> : <span className="muted" aria-label="zakázáno">–</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- přístupy všech ---------- */

const PAGE = 25;

export function AdminAudit() {
  const users = useAdminUsers();
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const q = useQuery({
    queryKey: ['admin', 'audit', page, userId, denied],
    staleTime: 0,
    placeholderData: (prev) => prev,
    queryFn: () =>
      api<{ entries: AuditEntryDTO[]; total: number }>('GET', `/admin/audit?page=${page}&pageSize=${PAGE}${userId ? `&userId=${userId}` : ''}${denied ? '&denied=1' : ''}`),
  });
  const total = q.data?.total ?? 0;
  const from = (page - 1) * PAGE + 1;
  return (
    <div className="set-list">
      <p className="muted">Přístupy k datům všech uživatelů (co Sam za koho načetl nebo zamítl). Text dotazů ani odpovědí se neukládá.</p>
      <div className="adm-bar">
        <div className="adm-filter">
          <Select
            clearable
            searchable
            options={(users.data ?? []).map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }))}
            value={userId}
            onChange={(v) => {
              setUserId(v);
              setPage(1);
            }}
            placeholder="Všichni uživatelé"
            aria-label="Filtr podle uživatele"
          />
        </div>
        <Checkbox
          label="Jen zamítnuté"
          checked={denied}
          onChange={(v) => {
            setDenied(v);
            setPage(1);
          }}
        />
      </div>
      {q.isLoading && <Spinner />}
      {q.error && <p className="bad">{(q.error as Error).message}</p>}
      {q.data && !q.data.entries.length && <EmptyState size="sm" title="Žádné záznamy" />}
      {!!q.data?.entries.length && (
        <div className={`audit${q.isPlaceholderData ? ' is-loading' : ''}`}>
          <table>
            <thead>
              <tr>
                <th>Čas</th>
                <th>Uživatel</th>
                <th>Nástroj</th>
                <th>Parametry</th>
                <th>Výsledek</th>
                <th className="r">Řádků</th>
                <th className="r">Doba</th>
              </tr>
            </thead>
            <tbody>
              {q.data.entries.map((a) => (
                <tr key={a.id}>
                  <td className="nowrap">{fmtDateTime(a.ts)}</td>
                  <td>
                    {a.userName ?? '–'}
                    <div className="muted small">{a.caller === 'ai' ? 'přes Sama' : a.caller === 'widget' ? 'pohled' : 'v appce'}</div>
                  </td>
                  <td>
                    {toolLabel(a.tool).done}
                    <div className="mono muted">{a.tool}</div>
                  </td>
                  <td className="mono">{fmtParams(a.params)}</td>
                  <td>
                    {a.decision === 'allow' ? <span className="ok">povoleno</span> : <span className="bad">zamítnuto</span>}
                    {a.reason && <div className="muted small">{reasonText(a.reason)}</div>}
                  </td>
                  <td className="r">{a.rows ?? '–'}</td>
                  <td className="r nowrap">{fmtMs(a.ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE && (
        <div className="audit-pager">
          <span className="muted small">
            {from}–{Math.min(from + PAGE - 1, total)} z {total}
          </span>
          <Pagination page={page} totalPages={Math.ceil(total / PAGE)} onChange={setPage} size="sm" />
        </div>
      )}
    </div>
  );
}

/* ---------- historie správy ---------- */

function eventText(e: AdminEventDTO): string {
  const d = e.details as { from?: Role; to?: Role; on?: boolean; role?: Role; via?: string };
  switch (e.action) {
    case 'user_create':
      return `přidal uživatele s rolí ${d.role ? ROLE_LABEL[d.role] : '–'}`;
    case 'role':
      return `změnil roli: ${d.from ? ROLE_LABEL[d.from] : '–'} → ${d.to ? ROLE_LABEL[d.to] : '–'}`;
    case 'admin':
      return `${d.on ? 'nastavil jako správce' : 'odebral správce'}${d.via === 'ADMIN_EMAILS' ? ' (z ADMIN_EMAILS)' : d.via === 'cli' ? ' (příkazem)' : ''}`;
    case 'block':
      return 'zablokoval přístup';
    case 'unblock':
      return 'odblokoval přístup';
    default:
      return e.action;
  }
}

export function AdminEvents() {
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['admin', 'events', page],
    staleTime: 0,
    placeholderData: (prev) => prev,
    queryFn: () => api<{ events: AdminEventDTO[]; total: number }>('GET', `/admin/events?page=${page}`),
  });
  const total = q.data?.total ?? 0;
  return (
    <div className="set-list">
      <p className="muted">Kdo komu co změnil ve správě. Záznamy nejde upravit ani smazat.</p>
      {q.isLoading && <Spinner />}
      {q.data && !q.data.events.length && <EmptyState size="sm" title="Zatím žádné změny" />}
      {q.data?.events.map((e) => (
        <div key={e.id} className="set-row">
          <div>
            <b>{e.target ?? '–'}</b>
            <p className="muted small">
              {e.actor ?? 'systém'} {eventText(e)}
            </p>
          </div>
          <span className="muted small nowrap">{fmtDateTime(e.ts)}</span>
        </div>
      ))}
      {total > 25 && (
        <div className="audit-pager">
          <span className="muted small">{total} změn</span>
          <Pagination page={page} totalPages={Math.ceil(total / 25)} onChange={setPage} size="sm" />
        </div>
      )}
    </div>
  );
}
